import Link from 'next/link';
import React from 'react';
import { IoChevronForward } from 'react-icons/io5';

import { Box, Group, MantineColor, Stack, Text, ThemeIcon, ThemeIconProps, UnstyledButton } from '@mantine/core';

import cxPanel from 'components/dashboard-panel/DashboardPanel.module.scss';

import { DashboardSkeletonBlock } from './DashboardSkeleton';

const THUMBNAIL_SIZE = 36;

const DashboardActionRow = ({
    icon,
    thumbnail,
    color = 'violet',
    variant = 'light',
    title,
    description,
    href,
    onClick,
    rightSection,
}: {
    icon?: React.ReactNode;
    thumbnail?: React.ReactNode;
    color?: MantineColor;
    variant?: ThemeIconProps['variant'];
    title: React.ReactNode;
    description?: React.ReactNode;
    href?: string;
    onClick?: () => void;
    rightSection?: React.ReactNode;
}) => {
    const content = (
        <Group gap="sm" wrap="nowrap">
            {thumbnail ? (
                <Box w={THUMBNAIL_SIZE} h={THUMBNAIL_SIZE} style={{ flexShrink: 0 }}>
                    {thumbnail}
                </Box>
            ) : (
                <ThemeIcon
                    color={color}
                    variant={variant}
                    size={THUMBNAIL_SIZE}
                    radius={99}
                    fz={18}
                    style={{ flexShrink: 0 }}
                >
                    {icon}
                </ThemeIcon>
            )}
            <Stack gap={0} miw={0} style={{ flexGrow: 1 }}>
                <Text component="div" size="sm" fw={600} truncate>
                    {title}
                </Text>
                {description && (
                    <Text component="div" size="xs" c="dimmed">
                        {description}
                    </Text>
                )}
            </Stack>
            {rightSection && <Box style={{ flexShrink: 0 }}>{rightSection}</Box>}
            {(href || onClick) && <IoChevronForward color="var(--mantine-color-gray-5)" style={{ flexShrink: 0 }} />}
        </Group>
    );

    if (href) {
        return (
            <Box component={Link} href={href} onClick={onClick} className={cxPanel.item}>
                {content}
            </Box>
        );
    }

    if (onClick) {
        return (
            <UnstyledButton onClick={onClick} className={cxPanel.item}>
                {content}
            </UnstyledButton>
        );
    }

    return <Box className={cxPanel.item}>{content}</Box>;
};

const DashboardActionRowSkeleton = () => (
    <Box className={cxPanel.item}>
        <Group gap="sm" wrap="nowrap">
            <DashboardSkeletonBlock height={THUMBNAIL_SIZE} width={THUMBNAIL_SIZE} circle style={{ flexShrink: 0 }} />
            <Stack gap={6} style={{ flexGrow: 1 }}>
                <DashboardSkeletonBlock height={12} width="60%" />
                <DashboardSkeletonBlock height={10} width="30%" />
            </Stack>
        </Group>
    </Box>
);

export { DashboardActionRow, DashboardActionRowSkeleton, THUMBNAIL_SIZE };
